import ApexCharts from "apexcharts";
import {
  Component, Input, Output, EventEmitter,
  OnChanges, SimpleChanges, OnDestroy,
  ViewChild, ElementRef, inject
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { BOGModalService } from "src/app/services/performance-service/bog-modal.service";
import { Subscription } from "rxjs";

export interface BogTopicItem {
  label: string;
  pct: number;
}

export interface BogSubTab {
  key: "health" | "safety" | "environment";
  label: string;
  iconSrc: string;
}

export interface BogPerformer {
  name: string;
  pct: string;
}

export interface BogFreqCard {
  label: string;
  sublabel: string;
  iconSrc: string;
  badgeClass: string;
  chartColor: string;
}

export interface BogMiniCard {
  title: string;
  subtitle: string;
}

export interface BogMainTab {
  key: "weTalk" | "iSee";
  label: string;
  iconSrc: string;
  iconWidth: number;
  iconHeight: number;
  dataPoints: number[];
}

@Component({
  selector: "app-bog-modal",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./bog-modal.component.html",
  styleUrls: ["./bog-modal.component.scss"],
})
export class BOGModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  @ViewChild("bogChartEl")      bogChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("coachingChartEl") coachingChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("ccChartEl")       ccChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("engCountryChartEl")    engCountryChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("engLeadershipChartEl") engLeadershipChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("engHseChartEl")        engHseChartEl!: ElementRef<HTMLDivElement>;

  private bogChart:      ApexCharts | null = null;
  private coachingChart: ApexCharts | null = null;
  private ccChart:       ApexCharts | null = null;
  private engCountryChart:    ApexCharts | null = null;
  private engLeadershipChart: ApexCharts | null = null;
  private engHseChart:        ApexCharts | null = null;

  private bogModalService = inject(BOGModalService);
  private dataSub: Subscription | null = null;

  activeMainTab: "weTalk" | "iSee" = "weTalk";
  activeCoachingSubTab: "health" | "safety" | "environment" = "health";
  activeCCSubTab:       "health" | "safety" | "environment" = "health";

  // ── Header ──────────────────────────────────────────────────────────
  headerIconSrc = "assets/performance/boots-on-ground.svg";
  headerTitle   = "Boots On Ground";

  // ── AI Box ──────────────────────────────────────────────────────────
  aiText = '';

  // ── BOG Mini Card ────────────────────────────────────────────────────
  bogMiniCard: BogMiniCard = {
    title:    "Boots on Ground",
    subtitle: '',
  };

  bogScore:     number   = 0;
  bogChartData: number[] = [];
  ccPoints:     number[] = [];

  bottomPerformersLabel = "Bottom Performers";
  bottomPerformers: BogPerformer[] = [];

  // ── Engagement Score (Country/Leadership/HSE) ─────────────────────────
  private engCountryMonths: any[] = [];
  private engPrevYearMonths: any[] = [];
  private engCurrentMonthName = '';
  private engCurrentYear = 0;

  /** Unit mode shows only the one Engagement Score chart (no Leadership/HSE split). */
  isUnitView = false;

  engCountryLabel    = 'Country';
  engLeadershipLabel = 'Leadership';
  engHseLabel        = 'HSE';

  engCountryAvgValue    = 0;
  engLeadershipAvgValue = 0;
  engHseAvgValue        = 0;

  engCountryMom:    number | null = null;
  engCountryYoy:    number | null = null;
  engLeadershipMom: number | null = null;
  engLeadershipYoy: number | null = null;
  engHseMom:        number | null = null;
  engHseYoy:        number | null = null;

  private engCountryValues:    number[] = [];
  private engLeadershipValues: number[] = [];
  private engHseValues:        number[] = [];
  private engPointColors:            string[] = [];
  private engLeadershipPointColors:  string[] = [];
  private engHsePointColors:         string[] = [];

  // ── Main Tabs ────────────────────────────────────────────────────────
  months: string[] = [];

  mainTabs: BogMainTab[] = [
    {
      key: "weTalk", label: "We Talk",
      iconSrc: "assets/performance/we-talk.svg",
      iconWidth: 32, iconHeight: 26,
      dataPoints: [],
    },
    {
      key: "iSee", label: "I See",
      iconSrc: "assets/performance/i-see.svg",
      iconWidth: 22, iconHeight: 22,
      dataPoints: [],
    },
  ];

  get activePoints(): number[] {
    return this.mainTabs.find(t => t.key === this.activeMainTab)?.dataPoints ?? [];
  }

  // ── Sub Tabs (shared definition) ─────────────────────────────────────
  subTabs: BogSubTab[] = [
    { key: "health",      label: "Health",      iconSrc: "assets/performance/health-shield.svg" },
    { key: "safety",      label: "Safety",      iconSrc: "assets/performance/safety-shield.svg" },
    { key: "environment", label: "Environment", iconSrc: "assets/performance/env-shield.svg"    },
  ];

  // ── Frequency Cards ──────────────────────────────────────────────────
  coachingCard: BogFreqCard = {
    label:      "COACHING & FEEDBACK",
    sublabel:   "Frequency rate",
    iconSrc:    "assets/performance/coaching-feedback.svg",
    badgeClass: "bog-freq-card__header-badge--teal",
    chartColor: "#12A9D5",
  };

  ccFreqCard: BogFreqCard = {
    label:      "CRITICAL CONTROLS",
    sublabel:   "Frequency rate",
    iconSrc:    "assets/performance/critical-controls-freq.svg",
    badgeClass: "bog-freq-card__header-badge--navy",
    chartColor: "#346EAF",
  };

  // ── Topics — Coaching & Feedback ─────────────────────────────────────
  coachingTopicsMap: Record<string, BogTopicItem[]> = {
    health: [], safety: [], environment: [],
  };

  // ── Topics — I See ────────────────────────────────────────────────────
  iSeeTopicsMap: Record<string, BogTopicItem[]> = {
    health: [], safety: [], environment: [],
  };

  // ── Topics — Critical Controls ────────────────────────────────────────
  ccTopicsMap: Record<string, BogTopicItem[]> = {
    health: [], safety: [], environment: [],
  };

  get coachingTopics(): BogTopicItem[] {
    if (this.activeMainTab === 'iSee') return this.iSeeTopicsMap[this.activeCoachingSubTab] ?? [];
    return this.coachingTopicsMap[this.activeCoachingSubTab];
  }
  get ccTopics(): BogTopicItem[] { return this.ccTopicsMap[this.activeCCSubTab]; }

  // ── Lifecycle ────────────────────────────────────────────────────────
  ngOnChanges(changes: SimpleChanges) {
    if (changes["isOpen"]) {
      const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
      if (ionContent) {
        if (this.isOpen) ionContent.style.setProperty("--overflow", "hidden");
        else             ionContent.style.removeProperty("--overflow");
      }
      if (this.isOpen) setTimeout(() => this.subscribeAndBuild(), 0);
      else {
        this.dataSub?.unsubscribe();
        this.dataSub = null;
        this.destroyCharts();
      }
    }
  }

  ngOnDestroy() {
    this.dataSub?.unsubscribe();
    this.destroyCharts();
  }

  close() { this.closeModal.emit(); }

  private subscribeAndBuild(): void {
    this.dataSub?.unsubscribe();
    this.dataSub = this.bogModalService.modalData$.subscribe((data) => {
      if (data) {
        this.aiText                        = data.aiText;
        this.bogMiniCard                   = { ...this.bogMiniCard, subtitle: data.bogSubtitle };
        this.bogScore                      = data.bogScore;
        this.bogChartData                  = data.bogChartData;
        this.bottomPerformers              = data.bottomPerformers;
        this.months                        = data.chartMonths;
        this.mainTabs[0].dataPoints        = data.weTalkPoints;
        this.mainTabs[1].dataPoints        = data.iSeePoints;
        this.ccPoints                      = data.ccPoints ?? [];
        this.coachingTopicsMap             = data.coachingTopicsMap;
        this.iSeeTopicsMap                 = data.iSeeTopicsMap;
        this.ccTopicsMap                   = data.ccTopicsMap;
        this.engCountryMonths              = data.engCountryMonths ?? [];
        this.engPrevYearMonths             = data.engPrevYearMonths ?? [];
        this.engCurrentMonthName           = data.engCurrentMonthName ?? '';
        this.engCurrentYear                = data.engCurrentYear ?? 0;
        this.isUnitView                    = data.isUnitMode ?? false;
        this.computeEngagementData();
      }
      this.buildCharts();
    });
  }

  // ── Tab switch ───────────────────────────────────────────────────────
  switchMainTab(tab: "weTalk" | "iSee") {
    this.activeMainTab = tab;
    setTimeout(() => this.rebuildFreqCharts(), 0);
  }

  // ── Chart builders ───────────────────────────────────────────────────
  private buildCharts() {
    this.destroyCharts();

    if (this.bogChartEl?.nativeElement) {
      this.bogChart = new ApexCharts(
        this.bogChartEl.nativeElement,
        this.sparklineOptions(this.bogChartData, this.lineColor(this.bogScore))
      );
      this.bogChart.render();
    }

    this.buildFreqChart(this.coachingChartEl, this.coachingCard.chartColor, "coaching", this.activePoints);
    this.buildFreqChart(this.ccChartEl,       this.ccFreqCard.chartColor,   "cc",       this.ccPoints);

    if (this.engCountryChartEl?.nativeElement) {
      this.engCountryChartEl.nativeElement.style.marginLeft = '-5.67px';
      this.engCountryChart = new ApexCharts(
        this.engCountryChartEl.nativeElement,
        this.engagementChartOptions(this.engCountryValues, this.engPointColors)
      );
      this.engCountryChart.render();
    }
    if (this.engLeadershipChartEl?.nativeElement) {
      this.engLeadershipChartEl.nativeElement.style.marginLeft = '-5.67px';
      this.engLeadershipChart = new ApexCharts(
        this.engLeadershipChartEl.nativeElement,
        this.engagementChartOptions(this.engLeadershipValues, this.engLeadershipPointColors)
      );
      this.engLeadershipChart.render();
    }
    if (this.engHseChartEl?.nativeElement) {
      this.engHseChartEl.nativeElement.style.marginLeft = '-5.67px';
      this.engHseChart = new ApexCharts(
        this.engHseChartEl.nativeElement,
        this.engagementChartOptions(this.engHseValues, this.engHsePointColors)
      );
      this.engHseChart.render();
    }
  }

  private buildFreqChart(ref: ElementRef<HTMLDivElement>, color: string, type: "coaching" | "cc", data: number[]) {
    if (!ref?.nativeElement) return;
    const chart = new ApexCharts(ref.nativeElement, this.freqChartOptions(data, color));
    chart.render();
    if (type === "coaching") this.coachingChart = chart;
    else                     this.ccChart = chart;
  }

  private rebuildFreqCharts() {
    this.coachingChart?.destroy(); this.coachingChart = null;
    this.ccChart?.destroy();       this.ccChart = null;
    this.buildFreqChart(this.coachingChartEl, this.coachingCard.chartColor, "coaching", this.activePoints);
    this.buildFreqChart(this.ccChartEl,       this.ccFreqCard.chartColor,   "cc",       this.ccPoints);
  }

  // ── Engagement Score (Country/Leadership/HSE) ─────────────────────────
  // Same trailing-6-month window / MoM / YoY / per-point status-color logic
  // as the mobile app's engagement.page.ts and the PDF report's country.html.
  private computeEngagementData(): void {
    const months = this.engCountryMonths.slice(-6);

    // Each sub-score has its own status/band — Country from engagement_status, Leadership
    // from slm_engagement_status, HSE from shield_engagement_status.
    this.engPointColors           = months.map((m: any) => this.getEngagementStatusColor(m.engagement_status));
    this.engLeadershipPointColors = months.map((m: any) => this.getEngagementStatusColor(m.slm_engagement_status));
    this.engHsePointColors        = months.map((m: any) => this.getEngagementStatusColor(m.shield_engagement_status));

    const period = this.engCurrentMonthName
      ? `${this.engCurrentMonthName.slice(0, 3)} ${this.engCurrentYear || ''}`
      : '';
    const countryOrUnitText = this.isUnitView ? 'Unit' : 'Country';
    this.engCountryLabel    = period ? `${countryOrUnitText} (${period})` : countryOrUnitText;
    this.engLeadershipLabel = period ? `Leadership (${period})` : 'Leadership';
    this.engHseLabel        = period ? `HSE (${period})` : 'HSE';

    this.engCountryValues    = months.map((m: any) => Number(m.final_engagement_score) || 0);
    this.engLeadershipValues = months.map((m: any) => Number(m.slm_eng_score) || 0);
    this.engHseValues        = months.map((m: any) => Number(m.shield_eng_score) || 0);

    // Average of the visible trailing-6-month window (not just the latest month) —
    // matches engagement.page.ts's updateEngagementScoreChart/setAvg behavior.
    const avgOf = (values: number[]): number => {
      const valid = values.filter((v) => Number.isFinite(v));
      return valid.length ? Math.round(valid.reduce((sum, v) => sum + v, 0) / valid.length) : 0;
    };
    this.engCountryAvgValue    = avgOf(this.engCountryValues);
    this.engLeadershipAvgValue = avgOf(this.engLeadershipValues);
    this.engHseAvgValue        = avgOf(this.engHseValues);

    ({ mom: this.engCountryMom, yoy: this.engCountryYoy } =
      this.computeMomYoy(months, 'final_engagement_score'));
    ({ mom: this.engLeadershipMom, yoy: this.engLeadershipYoy } =
      this.computeMomYoy(months, 'slm_eng_score'));
    ({ mom: this.engHseMom, yoy: this.engHseYoy } =
      this.computeMomYoy(months, 'shield_eng_score'));
  }

  // MoM: percent change vs the previous month within the trailing-6 window.
  // YoY: same month name looked up in engPrevYearMonths — stays null if that
  // doc/month doesn't exist.
  private computeMomYoy(months: any[], fieldKey: string): { mom: number | null; yoy: number | null } {
    const idx = months.findIndex((m: any) => m.month === this.engCurrentMonthName);
    let mom: number | null = null;
    if (idx >= 1) {
      const current = Number(months[idx][fieldKey]);
      const previous = Number(months[idx - 1][fieldKey]);
      if (Number.isFinite(current) && Number.isFinite(previous) && previous !== 0) {
        mom = Math.round(((current - previous) / previous) * 100);
      }
    }
    let yoy: number | null = null;
    if (idx >= 0) {
      const current = Number(months[idx][fieldKey]);
      const prevEntry = (this.engPrevYearMonths || []).find((m: any) => m.month === this.engCurrentMonthName);
      if (prevEntry) {
        const prevValue = Number(prevEntry[fieldKey]);
        if (Number.isFinite(current) && Number.isFinite(prevValue) && prevValue !== 0) {
          yoy = Math.round(((current - prevValue) / prevValue) * 100);
        }
      }
    }
    return { mom, yoy };
  }

  /** Maps engagement_status to the app's existing 5-tier score-band palette. */
  getEngagementStatusColor(status: string | null | undefined): string {
    switch ((status || '').trim().toLowerCase()) {
      case 'poor':        return '#9F0014';
      case 'fair':        return '#FFDE72';
      case 'good':        return '#94C12E';
      case 'very good':   return '#009146';
      case 'exceptional': return '#800080';
      default:            return '#9e9e9e';
    }
  }

  // Positive = up/green, negative = down/red — opposite of the shared BOG-style
  // arrow mapping, matching engagement.page.ts's getEngagementMomYoyArrow.
  getEngagementMomYoyArrow(value: number | null): 'up' | 'down' | null {
    if (value === null || value === undefined || Number.isNaN(value) || value === 0) return null;
    return value > 0 ? 'up' : 'down';
  }

  formatMomYoyDisplay(value: number | null): string {
    if (value === null || value === undefined || value === 0) return '0% -';
    return `${value > 0 ? '+' : ''}${value}%`;
  }

  private engagementChartOptions(values: number[], pointColors: string[]): any {
    const hasData = values.length > 0;

    // Same per-chart dynamic y-axis scaling as the PDF report's Engagement Score
    // card (buildEngagementChartOptions in the pdf_templates) — scaled to this
    // chart's own data range (with headroom) instead of a fixed/relative-to-max
    // scale, so the trend curve stays visible.
    const validValues = values.filter((v) => typeof v === 'number' && isFinite(v));
    const maxValue = validValues.length ? Math.max(...validValues) : 1000;
    const minValue = validValues.length ? Math.min(...validValues) : 0;
    let yMax = Math.ceil(maxValue + maxValue * 0.15);
    let yMin = Math.floor(minValue - minValue * 0.10);
    if (yMin < 0) yMin = 0;
    if (yMax <= yMin) yMax = yMin + 10;

    return {
      series: [{ name: '', data: values }],
      chart: {
        type: 'line', height: 60, width: '100%',
        background: 'linear-gradient(166deg, rgba(240,240,240,1) 3%, rgba(240,240,240,1) 44%, rgba(224,224,224,1) 63%, rgba(224,224,224,1) 84%)',
        toolbar: { show: false }, zoom: { enabled: false },
        animations: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: 'smooth', width: 1.5, colors: ['#9e9e9e'] },
      // numeric (not category) so the first/last points sit flush at the plot edges,
      // matching the PDF report's chart.
      xaxis: { type: 'numeric', labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { show: false, min: yMin, max: yMax },
      grid: { show: false, padding: { right: 10, left: 0, top: 8, bottom: 6 } },
      markers: {
        size: 2.5, colors: ['#9e9e9e'], strokeWidth: 0.5, strokeOpacity: 0.9,
        discrete: hasData
          ? values.map((_, i) => ({
              seriesIndex: 0, dataPointIndex: i,
              fillColor: pointColors[i] || '#9e9e9e', strokeColor: '#fff', size: 2.5,
            }))
          : [],
      },
      legend: { show: false }, dataLabels: { enabled: false },
      tooltip: { enabled: false }, colors: ['#9e9e9e'],
    };
  }

  private destroyCharts() {
    this.bogChart?.destroy();      this.bogChart = null;
    this.coachingChart?.destroy(); this.coachingChart = null;
    this.ccChart?.destroy();       this.ccChart = null;
    this.engCountryChart?.destroy();    this.engCountryChart = null;
    this.engLeadershipChart?.destroy(); this.engLeadershipChart = null;
    this.engHseChart?.destroy();        this.engHseChart = null;
  }

  // ── Sparkline ────────────────────────────────────────────────────────
  private sparklineOptions(data: number[], color: string): any {
    const max = data.length ? Math.max(...data) : 100;
    return {
      series: [{ name: "", data }],
      chart: {
        type: "line", height: 80, width: "100%",
        background: "transparent",
        toolbar: { show: false }, zoom: { enabled: false },
        animations: { enabled: false }, sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 1.5, colors: [color] },
      xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { show: false, min: 0, max: max * 1.45 },
      grid: { show: false, padding: { right: 36, left: 0, top: 8, bottom: 6 } },
      markers: {
        size: 2.5, colors: [color], strokeWidth: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0, fillColor: "transparent", strokeColor: "transparent", size: 0 },
        ],
      },
      legend: { show: false }, dataLabels: { enabled: false },
      tooltip: { enabled: false }, colors: [color],
    };
  }

  // ── Frequency chart ──────────────────────────────────────────────────
  private freqChartOptions(data: number[], color: string): any {
    const valid = data.filter(v => isFinite(v));
    const dataMin = Math.min(...valid), dataMax = Math.max(...valid);
    const range = dataMax - dataMin || 1;
    const pad = range * 0.25;
    return {
      series: [{ name: "", data }],
      chart: {
        type: "line", height: 140, width: "100%",
        toolbar: { autoSelected: "pan", show: false },
        zoom: { enabled: false },
        animations: {
          enabled: true, speed: 800,
          animateGradually: { enabled: true, delay: 2000 },
          dynamicAnimation: { enabled: true, speed: 1000 },
        },
      },
      stroke: { curve: "smooth", width: 1, colors: [color] },
      xaxis: {
        type: "category",
        categories: this.months,
        tickPlacement: "between",
        labels: {
          show: true, trim: false,
          hideOverlappingLabels: false, showDuplicates: true,
          rotate: -35, offsetY: -4,
          style: { colors: "#707070", fontSize: "11px", fontFamily: "Nunito Medium", fontWeight: 500, cssClass: "apexcharts-xaxis-label" },
        },
        axisTicks: { show: false },
        axisBorder: { show: true, color: "#e0e0e0", height: 1 },
        title: { text: "" },
      },
      yaxis: { show: false, min: dataMin - pad, max: dataMax + pad },
      grid: { show: false, padding: { left: 18, right: 5 } },
      dataLabels: {
        enabled: true, enabledOnSeries: [0],
        offsetY: -8, textAnchor: "middle",
        style: { fontSize: "9px", fontFamily: "Nunito Medium", fontWeight: 600, colors: ["#000"] },
        background: { enabled: false },
        dropShadow: { enabled: false },
        formatter: (val: number) => Math.round(val) + "%",
      },
      markers: {
        size: [2.5, 4], colors: [color],
        strokeColors: "#fff", strokeWidth: 1,
        strokeOpacity: 0.9, strokeDashArray: 0,
        fillOpacity: 1, shape: "circle",
        offsetX: 0, offsetY: 0,
      },
      legend: { show: false },
      tooltip: { enabled: false },
      colors: [color],
    };
  }

  // ── Color helpers ────────────────────────────────────────────────────
  lineColor(score: number): string {
    if (score < 55)   return "#9F0014";
    if (score < 85)   return "#FFDE72";
    if (score < 95)   return "#94C12E";
    if (score <= 100) return "#009146";
    return "#800080";
  }

  chartBg(score: number): string {
    if (score < 55)   return 'linear-gradient(166deg, rgb(245,229,231) 3%, rgb(245,229,231) 44%, rgb(236,206,209) 63%, rgb(236,206,209) 84%)';
    if (score < 85)   return 'linear-gradient(166deg, rgba(255,249,229,1) 3%, rgba(255,249,229,1) 44%, rgba(255,244,206,1) 63%, rgba(255,244,206,1) 84%)';
    if (score < 95)   return 'linear-gradient(166deg, rgba(244,249,234,1) 3%, rgba(244,249,234,1) 44%, rgba(234,243,215,1) 63%, rgba(234,243,215,1) 84%)';
    if (score <= 100) return 'linear-gradient(166deg, rgba(229,244,236,1) 3%, rgba(229,244,236,1) 44%, rgba(206,234,219,1) 63%, rgba(206,234,219,1) 84%)';
    return 'linear-gradient(166deg, rgba(239,229,245,1) 3%, rgba(239,229,245,1) 44%, rgba(225,206,236,1) 63%, rgba(225,206,236,1) 84%)';
  }
}
